// Sending one answer: POST /battles/{code}/answers with an AnswerRequest (shared/types.ts). Single-pick formats send
// { optionId } (hotspot regions and rarity picks too), number sends { value }, multi 'all' sends { optionIds } and
// order sends { order }. The server stamps the time it received the answer; that stamp, not the tap, is what scores.
import type { AnswerRequest, MeAnswer, PublicItem } from '@shared/types';
import { GRACE_MS } from '@shared/engine';
import { useGame } from '@/store/game';
import { api, ApiError, serverNow } from './api';
import { isOnline } from './online';
import { isSeatMoved } from './seat';

export type AnswerPayload = Omit<AnswerRequest, 'index'>;
type Q = Pick<PublicItem, 'format' | 'multi' | 'options'>;

/** True for the formats whose answer is a single option id. */
export function isSinglePick(q: Pick<PublicItem, 'format' | 'multi'>): boolean {
  if (q.format === 'number' || q.format === 'order') return false;
  if (q.format === 'multi') return q.multi?.mode === 'rarity';
  return true;
}

const ids = (v: unknown): string[] | undefined =>
  Array.isArray(v) && v.length > 0 && v.every((x) => typeof x === 'string' && x.length > 0) ? [...new Set(v as string[])] : undefined;

/**
 * The payload a format takes, from what it sent: only that format's field, checked. A bare option id (from an older
 * caller) is accepted for single picks. Returns undefined when the answer cannot be sent as it is.
 */
export function payloadFor(q: Q, raw: AnswerPayload | string | undefined): AnswerPayload | undefined {
  const a: AnswerPayload | undefined = typeof raw === 'string' ? { optionId: raw } : raw;
  if (!a) return undefined;
  if (q.format === 'number') return typeof a.value === 'number' && Number.isFinite(a.value) ? { value: a.value } : undefined;
  if (q.format === 'order') { const o = ids(a.order); return o ? { order: o } : undefined; }
  if (q.format === 'multi' && !isSinglePick(q)) {
    const picked = ids(a.optionIds);
    if (!picked) return undefined;
    // Sent in the order the options are shown, so the same picks always make the same request.
    const rank = new Map(q.options.map((o, i) => [o.id, i]));
    return { optionIds: [...picked].sort((x, y) => (rank.get(x) ?? 99) - (rank.get(y) ?? 99)) };
  }
  return typeof a.optionId === 'string' && a.optionId ? { optionId: a.optionId } : undefined;
}

/** The request body: the question index first, then the one field the format uses. */
export function answerBody(index: number, a: AnswerPayload): AnswerRequest {
  const body: AnswerRequest = { index };
  if (a.optionId !== undefined) body.optionId = a.optionId;
  if (a.value !== undefined) body.value = a.value;
  if (a.optionIds !== undefined) body.optionIds = a.optionIds;
  if (a.order !== undefined) body.order = a.order;
  return body;
}

/** The answer part of a stored lock-in or of a record read back from GET /me (whatever it carries). */
export function payloadOf(a: Partial<Pick<MeAnswer, 'optionId' | 'value' | 'optionIds' | 'order'>> | undefined): AnswerPayload | undefined {
  if (!a) return undefined;
  const out: AnswerPayload = {};
  if (typeof a.optionId === 'string' && a.optionId) out.optionId = a.optionId;
  if (typeof a.value === 'number' && Number.isFinite(a.value)) out.value = a.value;
  if (Array.isArray(a.optionIds) && a.optionIds.length) out.optionIds = [...a.optionIds];
  if (Array.isArray(a.order) && a.order.length) out.order = [...a.order];
  return Object.keys(out).length ? out : undefined;
}

/** "too late, the question has closed" → "Too late, the question has closed." */
export const sentence = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1).replace(/\.?$/, '.') : s);

/**
 * How one send ended, for the store's send state (store/game.ts SendState): ok is `sent` (with `duplicate`, the server
 * held an answer already, and `answer` is that one, as far as the server said: the phone shows it, never its own tap);
 * `late` is the window shut on it (refused as too late, the retries ran out of time, or no answer and no connection once
 * it had shut); `refused` is the server saying no to the answer itself; `offline` and `network` never reached the server
 * while the window was open, so the player can tap again (both are `failed`); `moved` is the seat taken by another
 * device (lib/seat), marked on the store for the game page to act on.
 */
export type SendResult =
  | { ok: true; elapsedMs?: number; duplicate?: boolean; answer?: AnswerPayload }
  | { ok: false; reason: 'late'; message: string; busy?: boolean }
  | { ok: false; reason: 'refused'; message: string }
  | { ok: false; reason: 'offline' }
  | { ok: false; reason: 'network' }
  | { ok: false; reason: 'moved' };

/** The duplicate 409's body: the answer the seat holds, as GET /me gives it while the question is open. */
const heldAnswer = (body: Record<string, unknown>): { answer?: AnswerPayload; elapsedMs?: number } => {
  const a = body.answer as Partial<MeAnswer> | undefined;
  if (!a || typeof a !== 'object') return {};
  const answer = payloadOf(a);
  return { ...(answer ? { answer } : {}), ...(typeof a.elapsedMs === 'number' ? { elapsedMs: a.elapsedMs } : {}) };
};

/** API Gateway's throttle (429) and the server's own trouble (5xx): the answer is worth sending again while the window is open. */
export const isBusyStatus = (status: number) => status === 429 || status >= 500;
/** Gaps before each new try after a busy reply, before jitter: soon at first, then further apart so a crowd of retries spreads out. */
export const BUSY_BACKOFF_MS = [300, 700, 1500, 2500] as const;
/** A try is sent only if it can still land before the window shuts (the deadline plus the grace), with this much to spare. */
export const LAND_MARGIN_MS = 150;
/** The server refuses an answer stamped before the question opened; when this phone's clock ran a touch ahead, it tries again this soon. */
export const NOT_OPEN_RETRY_MS = 250;
/** What the phone says when the answer never got through before the close: the server stayed too busy, or there was no signal in time. Calm, and no blame. */
export const BUSY_MESSAGE = 'Your answer did not get through before the close.';

const NOT_OPEN = /not open yet/i;
/** Refusals that mean the window has shut (or the question takes no more answers): there is nothing to tap again for. After a busy reply they get the same calm words. */
const CLOSED = /too late|no question is open|not the current one|was skipped/i;

/** The wait before busy retry number `attempt` (from 1): the backoff step with ±30% jitter. */
export function busyDelay(attempt: number, random: () => number = Math.random): number {
  const base = BUSY_BACKOFF_MS[Math.min(Math.max(1, attempt), BUSY_BACKOFF_MS.length) - 1];
  return Math.round(base * (0.7 + 0.6 * random()));
}

const pause = (ms: number) => new Promise<void>((r) => window.setTimeout(r, ms));
/** Seams for tests: how to wait, where the jitter comes from, and whether the phone has a connection. */
export interface SendDeps { pause?: (ms: number) => Promise<void>; random?: () => number; online?: () => boolean }

/**
 * Sends one answer. The server writes each answer once, so trying again is always safe:
 * - with no connection nothing is sent: the answer is not sent (the screen tries it once by itself when the connection
 *   comes back), or late if the window has already shut;
 * - a 429 or a 5xx is tried again with a jittered backoff for as long as a try can still land before the window shuts
 *   (the deadline plus the grace, never later); if none gets through, the answer is marked undelivered on this phone
 *   and the result is late, said calmly;
 * - an answer refused as "not open yet" (this phone's clock a little ahead of the server's) is sent again a moment later;
 * - a network failure is tried once more after 400 ms while the window is open, then comes back as not sent; one that
 *   lands once the window has shut (the request outlasted the deadline) is late, not something to tap again for;
 * - a refusal as too late (or for a question that takes no more answers) is late;
 * - a second tap that raced the first ("already answered") counts as sent, with the answer the server holds when the
 *   reply carries it, so a lost reply followed by a different tap ends with the server's pick on screen;
 * - the seat taken by another device (401 seat-moved) is marked on the store and comes back as `moved`;
 * - anything else the server says comes back as it is, as a sentence, and the options are free for another tap.
 */
export async function sendAnswer(p: { code: string; token: string; index: number; answer: AnswerPayload; deadlineAt: number }, deps: SendDeps = {}): Promise<SendResult> {
  const wait = deps.pause ?? pause; const random = deps.random ?? Math.random; const online = deps.online ?? isOnline;
  const body = JSON.stringify(answerBody(p.index, p.answer));
  const closesAt = p.deadlineAt + GRACE_MS;
  const open = () => serverNow() < closesAt;
  /** Time left to send a try that can still land in the window. */
  const room = () => closesAt - LAND_MARGIN_MS - serverNow();
  const late = (message = BUSY_MESSAGE): SendResult => ({ ok: false, reason: 'late', message });
  const gaveUp = (): SendResult => {
    useGame.getState().markUndelivered(p.index, 'busy');
    return { ok: false, reason: 'late', message: BUSY_MESSAGE, busy: true };
  };
  const noSignal = (): SendResult => (open() ? { ok: false, reason: 'offline' } : late());
  if (!online()) return noSignal();
  let networkTries = 0; let busyTries = 0;
  for (;;) {
    try {
      const r = await api<{ accepted?: boolean; elapsedMs?: number }>(`/battles/${p.code}/answers`, { method: 'POST', token: p.token, body });
      return { ok: true, elapsedMs: typeof r?.elapsedMs === 'number' ? r.elapsedMs : undefined };
    } catch (e) {
      if (e instanceof ApiError) {
        if (e.status === 409 && /already/i.test(e.message)) return { ok: true, duplicate: true, ...heldAnswer(e.body) };
        if (isSeatMoved(e)) { useGame.getState().markSeatMoved(); return { ok: false, reason: 'moved' }; }
        const early = e.status === 409 && NOT_OPEN.test(e.message);
        if (early || isBusyStatus(e.status)) {
          if (!early) busyTries++;
          const left = room();
          if (left > 0) { await wait(Math.min(early ? NOT_OPEN_RETRY_MS : busyDelay(busyTries, random), left)); continue; }
          if (busyTries > 0) return gaveUp();
        }
        if (e.status === 409 && CLOSED.test(e.message)) return busyTries > 0 ? gaveUp() : late(sentence(e.message));
        return { ok: false, reason: 'refused', message: sentence(e.message) };
      }
      // The connection went while the request was out: no point in a second try now.
      if (!online()) return noSignal();
      if (!open()) return late();
      if (networkTries++ === 0) { await wait(400); continue; }
      return { ok: false, reason: 'network' };
    }
  }
}
